"use client";

/**
 * 색상 컨트롤(텍스트 색/배경) — 컨텍스트별로 그룹 제목·툴팁을 명확히 구분한다.
 *
 * 배경:
 *  - BlockNote의 공유 `ColorPicker`는 그룹 제목을 editor 전역 dictionary
 *    (`color_picker.text_title`/`background_title`)에서 읽으므로, "글(텍스트) 배경"과
 *    "셀 배경"을 같은 "텍스트/배경" 라벨로 표기해 어떤 배경을 바꾸는 버튼인지 모호했다.
 *  - 공유 `ColorPicker`/`ColorIcon`은 패키지 루트로 export되지 않아 dictionary를
 *    덮어쓸 수도, 직접 import할 수도 없다(dictionary는 editor 인스턴스에 묶임).
 *  → 그래서 `ColorPicker`를 제목 prop을 받도록 복제(`LumirColorPicker`)하고,
 *    툴바(인라인)·셀 메뉴용 버튼을 각각 만들어 컨텍스트별 라벨을 주입한다.
 *
 * 라벨 규칙:
 *  - 툴바(인라인 글자 색/배경): 툴팁 "텍스트 색·배경", 그룹 "텍스트 색" / "텍스트 배경"
 *  - 셀 메뉴(셀 글자 색/배경):  트리거 "셀 색·배경",  그룹 "셀 글자색" / "셀 배경"
 *  버튼은 색·배경을 모두 바꾸므로 툴팁을 "배경"만으로 적으면 색 기능이 가려지고
 *  (특히 셀 메뉴의 텍스트 색), 그룹 제목에 "텍스트 배경"/"셀 배경"을 명시해 두 배경
 *  기능을 분명히 구분한다.
 */

import {
  type BlockSchema,
  type DefaultBlockSchema,
  type DefaultInlineContentSchema,
  type DefaultStyleSchema,
  type InlineContentSchema,
  isTableCell,
  mapTableCell,
  type StyleSchema,
} from "@blocknote/core";
import {
  SplitButton,
  type TableCellMenuProps,
  useBlockNoteEditor,
  useComponentsContext,
  useDictionary,
  useEditorContentOrSelectionChange,
  useSelectedBlocks,
} from "@blocknote/react";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import {
  getSelectedCellPositions,
  setCellAttrAtPositions,
} from "../../utils/prosemirror-table-utils";

const COLORS = [
  "default",
  "gray",
  "brown",
  "red",
  "orange",
  "yellow",
  "green",
  "blue",
  "purple",
  "pink",
] as const;

/** BlockNote 내부 ColorIcon이 export되지 않아 동일 구현(색 스와치 "A")을 복제. */
function ColorIcon(props: {
  textColor?: string;
  backgroundColor?: string;
  size?: number;
}) {
  const textColor = props.textColor || "default";
  const backgroundColor = props.backgroundColor || "default";
  const size = props.size || 16;
  return (
    <div
      className={"bn-color-icon"}
      data-background-color={backgroundColor}
      data-text-color={textColor}
      style={{
        pointerEvents: "none",
        fontSize: `${size * 0.75}px`,
        height: `${size}px`,
        lineHeight: `${size}px`,
        textAlign: "center",
        width: `${size}px`,
      }}
    >
      A
    </div>
  );
}

/** 셀 채움(배경) 아이콘 — 인라인 "A" 아이콘과 구분되는 페인트통(fill) 모양. */
function CellFillIcon({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ pointerEvents: "none" }}
      aria-hidden="true"
    >
      <path d="M16.56 8.94 7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10 10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z" />
    </svg>
  );
}

/**
 * 공유 ColorPicker의 복제판. 그룹 제목을 `textTitle`/`backgroundTitle`로 주입할 수
 * 있다(미지정 시 dictionary 기본값). data-test는 원본과 동일하게 유지.
 */
function LumirColorPicker(props: {
  onClick?: () => void;
  iconSize?: number;
  textTitle?: string;
  backgroundTitle?: string;
  text?: { color: string; setColor: (color: string) => void };
  background?: { color: string; setColor: (color: string) => void };
}) {
  const Components = useComponentsContext()!;
  const dict = useDictionary();

  return (
    <>
      {props.text ? (
        <>
          <Components.Generic.Menu.Label>
            {props.textTitle ?? dict.color_picker.text_title}
          </Components.Generic.Menu.Label>
          {COLORS.map((color) => (
            <Components.Generic.Menu.Item
              onClick={() => {
                props.onClick?.();
                props.text!.setColor(color);
              }}
              data-test={"text-color-" + color}
              icon={<ColorIcon textColor={color} size={props.iconSize} />}
              checked={props.text!.color === color}
              key={"text-color-" + color}
            >
              {dict.color_picker.colors[color]}
            </Components.Generic.Menu.Item>
          ))}
        </>
      ) : null}
      {props.background ? (
        <>
          <Components.Generic.Menu.Label>
            {props.backgroundTitle ?? dict.color_picker.background_title}
          </Components.Generic.Menu.Label>
          {COLORS.map((color) => (
            <Components.Generic.Menu.Item
              onClick={() => {
                props.onClick?.();
                props.background!.setColor(color);
              }}
              data-test={"background-color-" + color}
              icon={
                <ColorIcon backgroundColor={color} size={props.iconSize} />
              }
              checked={props.background!.color === color}
              key={"background-color-" + color}
            >
              {dict.color_picker.colors[color]}
            </Components.Generic.Menu.Item>
          ))}
        </>
      ) : null}
    </>
  );
}

/**
 * 툴바(인라인 텍스트) 색상 버튼. 글자 색 + 글자 배경을 바꾼다.
 * BlockNote ColorStyleButton의 복제판이며 그룹 제목/툴팁만 "텍스트"로 명시한다.
 */
export function LumirColorStyleButton() {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    BlockSchema,
    InlineContentSchema,
    StyleSchema
  >();

  // 기본 스타일 스키마(textColor/backgroundColor)는 제네릭 StyleSchema에서 모두
  // undefined로 추론되므로, 스타일 읽기/쓰기는 느슨한 타입 별칭으로 처리한다.
  const ed = editor as unknown as {
    getActiveStyles: () => Record<string, string>;
    addStyles: (styles: Record<string, string>) => void;
    removeStyles: (styles: Record<string, string>) => void;
  };

  const styleSchema = editor.schema.styleSchema as Record<
    string,
    { type?: string; propSchema?: string }
  >;
  const textColorInSchema =
    styleSchema.textColor?.type === "textColor" &&
    styleSchema.textColor?.propSchema === "string";
  const backgroundColorInSchema =
    styleSchema.backgroundColor?.type === "backgroundColor" &&
    styleSchema.backgroundColor?.propSchema === "string";

  const selectedBlocks = useSelectedBlocks(editor);

  const [currentTextColor, setCurrentTextColor] = useState<string>(
    textColorInSchema ? ed.getActiveStyles().textColor || "default" : "default",
  );
  const [currentBackgroundColor, setCurrentBackgroundColor] = useState<string>(
    backgroundColorInSchema
      ? ed.getActiveStyles().backgroundColor || "default"
      : "default",
  );

  useEditorContentOrSelectionChange(() => {
    const active = ed.getActiveStyles();
    if (textColorInSchema) {
      setCurrentTextColor(active.textColor || "default");
    }
    if (backgroundColorInSchema) {
      setCurrentBackgroundColor(active.backgroundColor || "default");
    }
  }, editor);

  const setTextColor = useCallback(
    (color: string) => {
      color === "default"
        ? ed.removeStyles({ textColor: color })
        : ed.addStyles({ textColor: color });
      // Mantine Toolbar의 useFocusTrap 호환을 위해 다음 틱에 focus.
      setTimeout(() => editor.focus());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor],
  );

  const setBackgroundColor = useCallback(
    (color: string) => {
      color === "default"
        ? ed.removeStyles({ backgroundColor: color })
        : ed.addStyles({ backgroundColor: color });
      setTimeout(() => editor.focus());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor],
  );

  const show = useMemo(() => {
    if (!textColorInSchema && !backgroundColorInSchema) {
      return false;
    }
    for (const block of selectedBlocks) {
      if (block.content !== undefined) {
        return true;
      }
    }
    return false;
  }, [backgroundColorInSchema, selectedBlocks, textColorInSchema]);

  if (!show || !editor.isEditable) {
    return null;
  }

  const tooltip = "텍스트 색·배경";

  return (
    <Components.Generic.Menu.Root>
      <Components.Generic.Menu.Trigger>
        <Components.FormattingToolbar.Button
          className={"bn-button"}
          data-test="colors"
          label={tooltip}
          mainTooltip={tooltip}
          icon={
            <ColorIcon
              textColor={currentTextColor}
              backgroundColor={currentBackgroundColor}
              size={20}
            />
          }
        />
      </Components.Generic.Menu.Trigger>
      <Components.Generic.Menu.Dropdown
        className={"bn-menu-dropdown bn-color-picker-dropdown"}
      >
        <LumirColorPicker
          textTitle="텍스트 색"
          backgroundTitle="텍스트 배경"
          text={
            textColorInSchema
              ? { color: currentTextColor, setColor: setTextColor }
              : undefined
          }
          background={
            backgroundColorInSchema
              ? { color: currentBackgroundColor, setColor: setBackgroundColor }
              : undefined
          }
        />
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  );
}

/**
 * 포매팅 툴바의 "셀 배경색" 버튼 — 여러 셀을 드래그(DND)로 선택했을 때 선택된
 * 모든 셀의 **배경색**을 일괄 변경한다(병합 버튼과 동일하게 다중 셀 선택에서만
 * 노출). 단일 셀은 셀 우측 그립 메뉴가 담당하므로 여기서는 숨긴다.
 *
 * 적용은 `setNodeMarkup`(셀 노드 attr) 방식이라 **셀 선택이 유지**된다 → 연속
 * 재색칠 가능(Notion 동작). updateBlock과 달리 내용 전체를 교체하지 않는다.
 *
 * 주의: 색 스와치 클릭은 드롭다운 상호작용이므로, 메뉴가 열리는 순간 셀 위치를
 * 스냅샷해 둔다. 적용 시 라이브 선택을 우선 쓰되(선택 유지), 혹시 붕괴했으면
 * 스냅샷으로 폴백해 색은 정확히 적용한다.
 *
 * 한계: 병합 셀(colspan/rowspan)은 이번 범위 외(예외만 방지).
 */
export function LumirCellColorToolbarButton() {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    { table: DefaultBlockSchema["table"] },
    InlineContentSchema,
    StyleSchema
  >();
  const selectedBlocks = useSelectedBlocks(editor);

  // 다중 셀 선택 여부(병합 버튼과 동일 패턴: selectedBlocks 변화 시 재계산).
  const isMultiCell = useMemo(() => {
    if (selectedBlocks.length !== 1 || selectedBlocks[0].type !== "table") {
      return false;
    }
    const cs = editor.tableHandles?.getCellSelection();
    return !!cs && cs.cells.length > 1;
  }, [editor, selectedBlocks]);

  // 메뉴 열림 시점의 셀 위치(pos) 스냅샷.
  const stashRef = useRef<number[]>([]);

  const applyBackground = useCallback(
    (color: string) => {
      // 라이브 선택 우선(선택 유지), 비어 있으면 스냅샷 폴백(색은 정확히 적용).
      const live = getSelectedCellPositions(editor);
      const positions = live.length > 0 ? live : stashRef.current;
      setCellAttrAtPositions(editor, positions, "backgroundColor", color);
      // 셀 선택이 다시 보이도록 에디터로 포커스 복귀.
      setTimeout(() => editor.focus());
    },
    [editor],
  );

  if (!editor.isEditable || !isMultiCell) {
    return null;
  }

  const tooltip = "셀 배경색";

  return (
    <Components.Generic.Menu.Root
      onOpenChange={(open: boolean) => {
        // 드롭다운이 열리는 순간 셀 위치 스냅샷(상호작용 중 선택 붕괴 대비).
        if (open) {
          stashRef.current = getSelectedCellPositions(editor);
        }
      }}
    >
      <Components.Generic.Menu.Trigger>
        <Components.FormattingToolbar.Button
          className={"bn-button"}
          data-test="cell-colors"
          label={tooltip}
          mainTooltip={tooltip}
          icon={<CellFillIcon size={18} />}
        />
      </Components.Generic.Menu.Trigger>
      <Components.Generic.Menu.Dropdown
        className={"bn-menu-dropdown bn-color-picker-dropdown"}
      >
        {/* 일괄 변경은 배경색만 제공(글자색 그룹 제거). */}
        <LumirColorPicker
          backgroundTitle="셀 배경"
          background={{ color: "default", setColor: applyBackground }}
        />
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  );
}

/**
 * 셀 메뉴의 색상 서브메뉴. 셀 글자 색 + 셀 배경을 바꾼다.
 * BlockNote 셀 ColorPickerButton의 복제판이며 트리거/그룹 제목만 "셀"로 명시한다.
 */
function LumirCellColorPickerButton<
  I extends InlineContentSchema = DefaultInlineContentSchema,
  S extends StyleSchema = DefaultStyleSchema,
>(props: TableCellMenuProps<I, S>) {
  const Components = useComponentsContext()!;
  const editor = useBlockNoteEditor<
    { table: DefaultBlockSchema["table"] },
    I,
    S
  >();

  const updateColor = (color: string, type: "text" | "background") => {
    const newTable = props.block.content.rows.map((row) => ({
      ...row,
      cells: row.cells.map((cell) => mapTableCell(cell)),
    }));

    if (type === "text") {
      newTable[props.rowIndex].cells[props.colIndex].props.textColor = color;
    } else {
      newTable[props.rowIndex].cells[props.colIndex].props.backgroundColor =
        color;
    }

    editor.updateBlock(props.block, {
      type: "table",
      content: { ...props.block.content, rows: newTable },
    });
    // updateBlock이 선택을 블록 밖으로 옮기므로 커서를 블록으로 되돌린다.
    editor.setTextCursorPosition(props.block);
  };

  const currentCell =
    props.block.content.rows[props.rowIndex]?.cells?.[props.colIndex];

  if (
    !currentCell ||
    (editor.settings.tables.cellTextColor === false &&
      editor.settings.tables.cellBackgroundColor === false)
  ) {
    return null;
  }

  return (
    <Components.Generic.Menu.Root position={"right"} sub={true}>
      <Components.Generic.Menu.Trigger sub={true}>
        <Components.Generic.Menu.Item className={"bn-menu-item"} subTrigger={true}>
          셀 색·배경
        </Components.Generic.Menu.Item>
      </Components.Generic.Menu.Trigger>

      <Components.Generic.Menu.Dropdown
        sub={true}
        className={"bn-menu-dropdown bn-color-picker-dropdown"}
      >
        <LumirColorPicker
          iconSize={18}
          textTitle="셀 글자색"
          backgroundTitle="셀 배경"
          text={
            editor.settings.tables.cellTextColor
              ? {
                  color: isTableCell(currentCell)
                    ? currentCell.props.textColor
                    : "default",
                  setColor: (color) => updateColor(color, "text"),
                }
              : undefined
          }
          background={
            editor.settings.tables.cellBackgroundColor
              ? {
                  color: isTableCell(currentCell)
                    ? currentCell.props.backgroundColor
                    : "default",
                  setColor: (color) => updateColor(color, "background"),
                }
              : undefined
          }
        />
      </Components.Generic.Menu.Dropdown>
    </Components.Generic.Menu.Root>
  );
}

/**
 * 셀 핸들 드롭다운 메뉴. 기본 TableCellMenu 복제판으로, 색상 버튼만 "셀" 라벨이
 * 명시된 LumirCellColorPickerButton으로 교체한다(병합 버튼은 그대로).
 * TableCellButton의 `tableCellMenu` prop으로 주입한다.
 */
export function LumirTableCellMenu<
  I extends InlineContentSchema = DefaultInlineContentSchema,
  S extends StyleSchema = DefaultStyleSchema,
>(props: TableCellMenuProps<I, S> & { children?: ReactNode }) {
  const Components = useComponentsContext()!;

  return (
    <Components.Generic.Menu.Dropdown
      className={"bn-menu-dropdown bn-drag-handle-menu"}
    >
      {props.children || (
        <>
          <SplitButton
            block={props.block}
            rowIndex={props.rowIndex}
            colIndex={props.colIndex}
          />
          <LumirCellColorPickerButton
            block={props.block}
            rowIndex={props.rowIndex}
            colIndex={props.colIndex}
          />
        </>
      )}
    </Components.Generic.Menu.Dropdown>
  );
}
